import React, { useState, useEffect } from 'react';
import { useI18n } from './i18n';
import { authFetch, isAuthenticated } from './auth';
import AttachmentList from './AttachmentList';
import BeerForm from './BeerForm';

const API_URL = window.location.hostname === 'localhost'
  ? 'http://localhost:8100/api'
  : '/api';

function BeerDetail({ beerId, meId, onBack, onEdit, onDeleted }) {
  const { t, formatNumber } = useI18n();
  const [beer, setBeer] = useState(null);
  const [error, setError] = useState(false);
  const [note, setNote] = useState(null);

  useEffect(() => {
    let abort = false;
    authFetch(`${API_URL}/beers/${beerId}`)
      .then((r) => (r && r.ok ? r.json() : Promise.reject(r && r.status)))
      .then((b) => { if (!abort) { setBeer(b); } })
      .catch(() => { if (!abort) setError(true); });
    return () => { abort = true; };
  }, [beerId]);

  const back = <button className="back-btn" onClick={onBack}>{t('beers.back')}</button>;
  if (error) return <div className="catalog">{back}<p>{t('beers.err_load')}</p></div>;
  if (!beer) return <div className="catalog">{back}<p>{t('ui.loading')}</p></div>;

  const mine = !!meId && beer.owner_id === meId;
  const remove = async () => {
    if (!window.confirm(t('beers.confirm_delete'))) return;
    try {
      const res = await authFetch(`${API_URL}/beers/${beer.id}`, { method: 'DELETE' });
      const data = res ? await res.json().catch(() => ({})) : {};
      if (!res || !res.ok) throw new Error(data.detail || '');
      onDeleted();
    } catch (e) {
      setNote({ type: 'error', text: e.message || t('beers.delete_error') });
    }
  };

  return (
    <div className="catalog">
      {back}
      <div className="wine-card">
        <h2>{beer.name}</h2>
        {beer.brewery && <p><strong>{t('beers.brewery')}:</strong> {beer.brewery}</p>}
        <div className="wine-meta">
          {beer.style && <span><strong>{t('beers.style')}:</strong> {beer.style}</span>}
          {beer.abv != null && <span><strong>{t('beers.abv')}:</strong> {formatNumber(beer.abv)}%</span>}
          {beer.volume_ml != null && <span><strong>{t('beers.volume')}:</strong> {formatNumber(beer.volume_ml, 0)} ml</span>}
          {beer.ibu != null && <span><strong>{t('beers.ibu')}:</strong> {formatNumber(beer.ibu, 0)}</span>}
        </div>
      </div>
      {note && <div className={`message ${note.type}`}>{note.text}</div>}
      {mine ? (
        <div className="form-buttons">
          <button type="button" className="submit-btn" onClick={() => onEdit(beer)}>{t('beers.edit')}</button>
          <button type="button" className="reset-btn" onClick={remove}>{t('beers.delete')}</button>
        </div>
      ) : <p className="empty">{t(beer.owner_id ? 'beers.readonly_other' : 'beers.readonly_anonymous')}</p>}

      <h3>{t('beers.labels')}</h3>
      <AttachmentList ownerType="beers" ownerId={beer.id} canEdit={isAuthenticated()} />
    </div>
  );
}

function BeerCatalog() {
  const { t } = useI18n();
  const [query, setQuery] = useState('');
  const [beers, setBeers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [mode, setMode] = useState({ name: 'list' });       // list | detail | new | edit
  const [meId, setMeId] = useState(null);
  const [reload, setReload] = useState(0);

  useEffect(() => {
    if (!isAuthenticated()) return;
    authFetch(`${API_URL}/me`)
      .then((r) => (r && r.ok ? r.json() : null))
      .then((u) => { if (u) setMeId(u.id); })
      .catch(() => {});
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    const timer = setTimeout(async () => {
      setLoading(true);
      try {
        const res = await fetch(`${API_URL}/beers${query ? `?q=${encodeURIComponent(query)}` : ''}`, { signal: controller.signal });
        if (res.ok) setBeers(await res.json());
      } catch { /* aborted */ } finally { setLoading(false); }
    }, query ? 250 : 0);
    return () => { clearTimeout(timer); controller.abort(); };
  }, [query, reload]);

  const toList = () => { setMode({ name: 'list' }); setReload((n) => n + 1); };

  if (mode.name === 'new') {
    return <div className="catalog"><BeerForm onCancel={toList} onDone={(b) => { setReload((n) => n + 1); setMode({ name: 'detail', id: b.id }); }} /></div>;
  }
  if (mode.name === 'edit') {
    return <div className="catalog"><BeerForm beer={mode.beer} onCancel={() => setMode({ name: 'detail', id: mode.beer.id })}
      onDone={(b) => { setReload((n) => n + 1); setMode({ name: 'detail', id: b.id }); }} /></div>;
  }
  if (mode.name === 'detail') {
    return <BeerDetail key={`${mode.id}-${reload}`} beerId={mode.id} meId={meId} onBack={toList}
      onEdit={(b) => setMode({ name: 'edit', beer: b })} onDeleted={toList} />;
  }

  return (
    <div className="catalog">
      <h2>{t('beers.title')}</h2>
      <div className="catalog-search">
        <input type="search" placeholder={t('beers.search')} value={query} onChange={(e) => setQuery(e.target.value)} />
      </div>
      <p><button type="button" className="submit-btn" onClick={() => setMode({ name: 'new' })}>{t('beers.add')}</button></p>
      {loading && <p className="empty">{t('ui.loading')}</p>}
      {!loading && beers.length === 0 && <p className="empty">{t('beers.none')}</p>}
      {beers.length > 0 && (
        <table className="catalog-table">
          <thead>
            <tr>
              <th>{t('beers.th_name')}</th>
              <th>{t('beers.brewery')}</th>
              <th>{t('beers.style')}</th>
              <th>{t('beers.abv')}</th>
            </tr>
          </thead>
          <tbody>
            {beers.map((b) => (
              <tr key={b.id} onClick={() => setMode({ name: 'detail', id: b.id })}>
                <td><strong>{b.name}</strong></td>
                <td>{b.brewery || '—'}</td>
                <td>{b.style || '—'}</td>
                <td>{b.abv != null ? `${b.abv}%` : '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

export default BeerCatalog;
